import { motion } from "framer-motion";
import { Layers, FileSpreadsheet, KeyRound, Zap, History, ShieldCheck } from "lucide-react";

const ANALYTICS_IMG = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxzZW8lMjBkYXRhJTIwYW5hbHl0aWNzJTIwY29kZSUyMHNjcmVlbnxlbnwwfHx8fDE3OTE4NjQxMzh8MA&ixlib=rb-4.1.0&q=85";
const CODE_IMG = "https://images.unsplash.com/photo-1542831371-29b0f74f9713?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHxfHxzZW8lMjBkYXRhJTIwYW5hbHl0aWNzJTIwY29kZSUyMHNjcmVlbnxlbnwwfHx8fDE3OTE4NjQxMzh8MA&ixlib=rb-4.1.0&q=85";

const features = [
  {
    icon: Layers,
    title: "High-Throughput Bulk Queries",
    body: "Fifty domains per run, deduplicated and normalized automatically. Screen an entire prospect list before your coffee cools.",
    testid: "feature-card-bulk-processing",
    span: "md:col-span-2",
    img: ANALYTICS_IMG,
  },
  {
    icon: FileSpreadsheet,
    title: "Clean CSV Exports",
    body: "One click, reporting-ready.",
    testid: "feature-card-csv-export",
  },
  {
    icon: KeyRound,
    title: "Live MOZ API Integration",
    body: "Plug in your own MOZ API token and every check runs against the live link index — no rebuild required.",
    testid: "feature-card-moz-api",
    img: CODE_IMG,
  },
  {
    icon: Zap,
    title: "Zero-Lag Interface",
    body: "Results stream in row by row.",
    testid: "feature-card-speed",
  },
  {
    icon: History,
    title: "Nothing Stored",
    body: "No accounts, no logs, no history. Your prospect lists stay yours.",
    testid: "feature-card-privacy",
  },
  {
    icon: ShieldCheck,
    title: "Spam-First Screening",
    body: "High-risk domains are flagged red instantly.",
    testid: "feature-card-spam-flag",
  },
];

export const Features = () => (
  <section id="features" data-testid="features-bento-section" className="py-24 sm:py-32 border-t border-[#263248]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl"
      >
        <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#D4FF00] mb-4">// Why SerpBacklink</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F3F4F6]">
          Built for the daily research grind
        </h2>
      </motion.div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
        {features.map((f, i) => (
          <motion.div
            key={f.testid}
            data-testid={f.testid}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
            className={`relative overflow-hidden border border-[#263248] bg-[#121824] rounded-xl p-7 group hover:border-[#D4FF00]/40 transition-colors ${f.span || ""}`}
          >
            {f.img && (
              <div className="absolute inset-0 opacity-[0.08] group-hover:opacity-[0.14] transition-opacity">
                <img src={f.img} alt="" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121824] via-transparent to-transparent" />
              </div>
            )}
            <div className="relative">
              <f.icon size={22} className="text-[#D4FF00]" />
              <h3 className="mt-5 font-display text-xl font-bold text-[#F3F4F6]">{f.title}</h3>
              <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed max-w-md">{f.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
